import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Clock, DollarSign, CalendarCheck, ShieldCheck, MessageCircle, Camera, Check } from "lucide-react";

const STEPS = [
  { icon: Camera, t: "Add your space", d: "A few photos, the address, and what's inside — drums, a PA, mirrors, seating. Takes about 5 minutes." },
  { icon: Clock, t: "Set your hours & rate", d: "Choose the blocks you're free (Tue 7–10pm, weekend mornings) and your hourly price. You stay in control." },
  { icon: DollarSign, t: "Get booked & paid", d: "Renters pay securely up front. Your slot is held, and your earnings land in your dashboard." },
];

const NEED = [
  "2–5 photos of the room",
  "The address & how renters get in",
  "Your hourly rate (you set it)",
  "The hours you're happy to rent",
  "Optional: a refundable deposit",
];

const WHY = [
  { icon: DollarSign, t: "Turn dead hours into income", d: "That empty Tuesday night or quiet weekday morning becomes revenue — with zero new overhead." },
  { icon: ShieldCheck, t: "Protected & verified", d: "Renters verify their ID, you can require a refundable deposit, and file a claim with photos if anything's off." },
  { icon: MessageCircle, t: "Coordinate in one place", d: "Message every renter inside the booking to sort load-in, keys, and quiet hours before they arrive." },
];

const FAQ = [
  { q: "What does it cost to list?", d: "Nothing. Listing is free — SpareHours adds a 15% service fee for renters on top of your rate, so you keep the price you set." },
  { q: "Who can book my space?", d: "Only ID-verified renters. You'll see who booked, and you can message them before they arrive." },
  { q: "What if something gets damaged?", d: "Require a refundable deposit at booking. If there's damage, file a claim with photos and keep what's needed — the rest is refunded automatically." },
  { q: "Can I control when it's available?", d: "Yes. You only offer the hours you choose, and overlapping bookings are blocked automatically so you're never double-booked." },
];

export default function ListYourSpace() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const start = () => navigate(user ? "/host/new" : "/signup");

  return (
    <div data-testid="list-your-space-page">
      {/* HERO */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FF4500] mb-5">FOR SPACE OWNERS</div>
          <div className="inline-flex items-center gap-2 bg-[#FF4500]/15 border border-[#FF4500]/30 text-[#FF4500] rounded-full px-4 py-1.5 text-xs font-semibold mb-6" data-testid="founding-banner">
            Founding Hosts: list free & pay 0% fees for your first 3 months
          </div>
          <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-3xl">
            Your empty hours are<br /><span className="text-[#FF4500]">worth money.</span>
          </h1>
          <p className="mt-6 text-lg text-white/80 max-w-xl leading-relaxed">
            Rent the hours your space sits unused — to musicians, dancers, photographers, and teams who just need a room by the hour. List free in about five minutes.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button onClick={start} className="bg-[#FF4500] hover:bg-[#E63E00] text-white rounded-full h-12 px-8 font-medium" data-testid="lys-hero-cta">
              {user ? "List your space" : "Get started — it's free"}
            </Button>
            <span className="text-sm text-white/50">No listing fee · You set the price</span>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-3 gap-6">
        {WHY.map((f) => (
          <div key={f.t} className="border border-neutral-200 rounded-xl p-6 bg-white">
            <f.icon className="w-6 h-6 text-[#FF4500]" />
            <div className="font-display font-bold text-lg mt-4">{f.t}</div>
            <div className="text-sm text-neutral-600 mt-1 leading-relaxed">{f.d}</div>
          </div>
        ))}
      </section>

      {/* STEPS */}
      <section className="bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-3">HOW IT WORKS</div>
          <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl mb-10">Listing takes about five minutes</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <div key={s.t}>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm text-[#FF4500]">0{i + 1}</span>
                  <s.icon className="w-5 h-5 text-neutral-700" />
                </div>
                <div className="font-display font-bold text-xl mt-3">{s.t}</div>
                <div className="text-sm text-neutral-600 mt-1 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU NEED */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-3">BEFORE YOU START</div>
          <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl">What you'll need</h2>
          <p className="text-neutral-600 mt-3 leading-relaxed">Have these handy and you'll be live in minutes. You can edit anything later.</p>
        </div>
        <ul className="space-y-3">
          {NEED.map((n) => (
            <li key={n} className="flex items-center gap-3 text-neutral-800">
              <span className="w-6 h-6 rounded-full bg-[#FF4500]/10 grid place-items-center shrink-0">
                <Check className="w-3.5 h-3.5 text-[#FF4500]" />
              </span>
              {n}
            </li>
          ))}
        </ul>
      </section>

      {/* FAQ */}
      <section className="max-w-[900px] mx-auto px-6 lg:px-10 pb-8">
        <h2 className="font-display font-black tracking-tighter text-3xl mb-8">Questions, answered</h2>
        <div className="divide-y divide-neutral-200 border-t border-neutral-200">
          {FAQ.map((f) => (
            <div key={f.q} className="py-6">
              <div className="font-display font-bold text-lg flex items-center gap-2"><CalendarCheck className="w-4 h-4 text-[#FF4500]" />{f.q}</div>
              <p className="text-neutral-600 mt-2 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 pb-24">
        <div className="bg-[#0A0A0A] text-white rounded-2xl p-10 lg:p-14 text-center">
          <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl">Ready to fill your empty hours?</h2>
          <p className="text-white/70 mt-3 max-w-xl mx-auto">List free, set your own hours, and start earning from the time your space already sits empty.</p>
          <Button onClick={start} className="mt-8 bg-[#FF4500] hover:bg-[#E63E00] text-white rounded-full h-12 px-10 font-medium" data-testid="lys-footer-cta">
            {user ? "List your space" : "Create your free account"}
          </Button>
        </div>
      </section>
    </div>
  );
}
